import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward, Assignment, Close, Folder } from '@mui/icons-material';
import { dashboardService, MyEvolutionsPanel, MyEvolutionsPatient } from '../../services/dashboardService';
import { titleCaseName } from '../../utils/nameCase';
import { colors } from '../../theme/designSystem';

/**
 * Painel "Minhas evoluções" da página inicial do Profissional (RN-216).
 *
 * É o contraponto operacional do relatório de Produtividade (RN-215): aquele é a visão da
 * coordenação sobre todos; este é a do terapeuta sobre si mesmo — e é ele que fecha a alça,
 * porque quem registra a evolução é o próprio profissional.
 *
 * <p>O donut mostra o PENDENTE, não o evoluído: o painel existe para cobrar o que falta, e é o
 * número que precisa incomodar. Some da tela quando não há nada realizado no período — painel
 * zerado na home não informa nada e ocupa a dobra de quem está começando o mês.</p>
 */

/** Quantos pacientes a lista mostra antes de oferecer o restante na modal. */
const NA_PREVIA = 6;


/** Dentro do `.dashboard-group` o painel NÃO leva sombra própria: o container já é a caixa, e
 *  o CSS do grupo zera sombra e padding de qualquer `Paper` ali dentro (por isso aqui é `Box`).
 *  Caixa com sombra dentro de caixa com sombra é o que destoava dos painéis antigos. */
const cardSx = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e9ecef',
  padding: '1.25rem',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const ddmm = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** "há 4 min" — a idade do dado, para ninguém tomar decisão sobre um painel velho. */
const desdeQuando = (carregadoEm: number): string => {
  const min = Math.floor((Date.now() - carregadoEm) / 60000);
  if (min < 1) return 'agora';
  if (min === 1) return 'há 1 min';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  return h === 1 ? 'há 1 hora' : `há ${h} horas`;
};

/**
 * Donut no MESMO desenho dos cards do topo: 120px, `viewBox 0 0 42 42`, raio 15.915 e
 * `strokeWidth 3`. O meu estava em 132px com traço 4 e saltava aos olhos ao lado dos outros.
 */
const Donut: React.FC<{ pct: number }> = ({ pct }) => (
  <Box sx={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
    <svg width="120" height="120" viewBox="0 0 42 42" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
      {pct > 0 && (
        <circle
          cx="21"
          cy="21"
          r="15.915"
          fill="transparent"
          stroke={colors.error}
          strokeWidth="3"
          strokeDasharray={`${pct} ${100 - pct}`}
          strokeLinecap="round"
        />
      )}
    </svg>
    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.error }}>
        {pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>pendente</Typography>
    </Box>
  </Box>
);

/** Uma linha da fila. `posicao` numera para dar noção de fila, não de ranking. */
const LinhaPaciente: React.FC<{
  item: MyEvolutionsPatient;
  posicao: number;
  onAbrir: (id: string) => void;
}> = ({ item, posicao, onAbrir }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      // no celular vira cartão: número e nome na primeira linha, chip, idade e botão na segunda
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      gap: { xs: 1, sm: 2 },
      py: 1.35,
      px: 0.5,
      borderBottom: '1px solid #f1f3f5',
      '&:hover': { backgroundColor: '#fafbfc' },
    }}
  >
    <Typography sx={{ fontSize: 11, color: '#c3cbd3', fontVariantNumeric: 'tabular-nums', width: 20, flexShrink: 0 }}>
      {String(posicao).padStart(2, '0')}
    </Typography>
    <Typography
      sx={{ fontSize: '0.875rem', color: '#212529', flex: { xs: '1 1 calc(100% - 28px)', sm: 1 }, minWidth: 0, overflowWrap: 'anywhere' }}
    >
      {titleCaseName(item.patientName)}
    </Typography>
    <Chip
      size="small"
      label={`${item.pending} ${item.pending === 1 ? 'sessão' : 'sessões'}`}
      sx={{ height: 22, fontSize: 11.5, backgroundColor: colors.errorSoft, color: colors.error, fontWeight: 600, flexShrink: 0 }}
    />
    <Typography
      sx={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap', width: { xs: 'auto', sm: 52 }, textAlign: 'right', flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}
    >
      {item.oldestDays === 0 ? 'hoje' : `há ${item.oldestDays}d`}
    </Typography>
    {/* Mesmo botão de acesso ao cadastro do resto do dashboard (azul sólido) — antes o acesso
        era só o nome clicável, que não se anuncia como ação. */}
    <Tooltip title="Abrir cadastro do paciente">
      <IconButton
        onClick={() => onAbrir(item.patientId)}
        size="small"
        sx={{ bgcolor: '#03a9f4', color: 'white', flexShrink: 0, '&:hover': { bgcolor: '#0288d1' } }}
      >
        <Folder fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const MyEvolutionsWidget: React.FC<{
  onOpenPatient: (patientId: string) => void;
  /** Painel no TOPO do dashboard (perfil Profissional): dispensa a margem que o separa do bloco
   *  anterior, porque não há bloco anterior. */
  first?: boolean;
}> = ({ onOpenPatient, first = false }) => {
  const [data, setData] = useState<MyEvolutionsPanel | null>(null);
  const [loading, setLoading] = useState(true);
  const [carregadoEm, setCarregadoEm] = useState(Date.now());
  const [listaAberta, setListaAberta] = useState(false);
  // só para redesenhar o "atualizado há X min" enquanto a tela fica aberta
  const [, setTick] = useState(0);

  const carregar = useCallback(() => {
    dashboardService
      .myEvolutions()
      .then((d) => {
        setData(d);
        setCarregadoEm(Date.now());
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <Box className="dashboard-group" sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Nada realizado no período: o painel não aparece, em vez de exibir uma fileira de zeros.
  if (!data || data.attended === 0) {
    return null;
  }

  const emDia = data.pending === 0;

  return (
    // O container `dashboard-group` é DESTE componente, não do Dashboard: quando não há nada a
    // mostrar o painel inteiro some. Com o wrapper por fora, sobrava uma caixa vazia com borda
    // para quem não atende (achado do Hilton, 11/09).
    <Box className="dashboard-group" sx={{ mt: first ? 0 : 3 }}>
      {/* Cabeçalho no MESMO padrão das outras áreas do dashboard: ícone redondo + h5 de
          1,25rem (ver "Análise de cancelamentos e evasão"), para o painel não parecer de outro
          sistema. O período e a idade do dado são apoio, em corpo menor. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Azul, e não vermelho: o bloco seguinte (Cancelamentos) já tem ícone vermelho, e
              dois vermelhos seguidos na mesma rolagem tiram o peso de alerta dos dois. O
              vermelho aqui fica só onde significa pendência — o donut e os números. */}
          <Box className="dashboard-card-icon blue">
            <Assignment />
          </Box>
          <Box>
            {/* MESMO título dos cards do topo: h6 + `dashboard-card-title` em 1rem/600.
                Estava em h5/1,25rem e destoava dos blocos de cima na mesma rolagem. */}
            <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>
              Minhas evoluções
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
              {ddmm(data.from)} a {ddmm(data.to)} · atualizado {desdeQuando(carregadoEm)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 5fr) minmax(0, 7fr)' }, gap: 2, alignItems: 'start' }}>
        {/* ================= coluna da esquerda ================= */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* `flexDirection: row` explícito: o cardSx é coluna (para as caixas terminarem na
              mesma linha) e sem isto o donut ficava EM CIMA dos números, não ao lado. */}
          <Box sx={{ ...cardSx, flexDirection: 'row', alignItems: 'center', gap: 2.5 }}>
            <Donut pct={emDia ? 0 : data.pendingRate} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {[
                { rotulo: 'Realizados', valor: data.attended, cor: '#1f2933' },
                { rotulo: 'Evoluídos', valor: data.evolved, cor: emDia ? colors.success : '#1f2933' },
                { rotulo: 'Pendentes', valor: data.pending, cor: emDia ? '#adb5bd' : colors.error },
              ].map((l, i) => (
                <Box
                  key={l.rotulo}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    py: 1.1,
                    borderTop: i === 0 ? 'none' : '1px solid #f1f3f5',
                  }}
                >
                  <Typography sx={{ fontSize: '0.875rem', color: '#6c757d' }}>{l.rotulo}</Typography>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: l.cor, fontVariantNumeric: 'tabular-nums' }}>
                    {l.valor}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {data.byService.length > 0 && (
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#212529', mb: 1.5 }}>
                Por tipo de serviço
              </Typography>
              {data.byService.map((s) => (
                <Box key={s.serviceName} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.6 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#212529', overflowWrap: 'anywhere' }}>
                      {s.serviceName}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#8d99a6', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {s.evolved}/{s.attended}
                      {' · '}
                      <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: s.evolvedRate > 0 ? colors.success : '#c3cbd3' }}>
                        {s.evolvedRate}%
                      </Typography>
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, backgroundColor: '#eef1f4', overflow: 'hidden' }}>
                    <Box sx={{ width: `${s.evolvedRate}%`, height: '100%', backgroundColor: colors.success, transition: 'width .3s' }} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

        </Box>

        {/* ================= coluna da direita ================= */}
        <Box sx={cardSx}>
          {emDia ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: colors.success }}>
                Tudo evoluído no período.
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6c757d', mt: 0.5 }}>
                {data.attended} {data.attended === 1 ? 'atendimento realizado' : 'atendimentos realizados'}, nenhum pendente.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: colors.error, lineHeight: 1 }}>
                  {data.patients.length}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#212529' }}>
                  {data.patients.length === 1 ? 'paciente com evolução pendente' : 'pacientes com evolução pendente'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', ml: 'auto' }}>
                  mais antigos primeiro
                </Typography>
              </Box>

              {data.patients.slice(0, NA_PREVIA).map((p, i) => (
                <LinhaPaciente key={p.patientId} item={p} posicao={i + 1} onAbrir={onOpenPatient} />
              ))}

              {data.patients.length > NA_PREVIA && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.25 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                    + {data.patients.length - NA_PREVIA} pacientes
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    onClick={() => setListaAberta(true)}
                    sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                  >
                    Ver lista completa
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Lista inteira em modal: o painel da home não pode crescer sem limite, e a fila do
          terapeuta às vezes passa de vinte pacientes. */}
      <Dialog open={listaAberta} onClose={() => setListaAberta(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 17, fontWeight: 700 }}>
          Evoluções pendentes
          <Typography component="span" sx={{ fontSize: 13, color: '#8d99a6', fontWeight: 400 }}>
            {data.patients.length} pacientes · {data.pending} sessões
          </Typography>
          <IconButton onClick={() => setListaAberta(false)} size="small" sx={{ ml: 'auto' }} aria-label="Fechar">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {data.patients.map((p, i) => (
            <LinhaPaciente key={p.patientId} item={p} posicao={i + 1} onAbrir={onOpenPatient} />
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MyEvolutionsWidget;
